import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { AppDialog } from '../Common/AppDialog'
import type { ChecklistItem } from '../../types/schema'

export interface BoardDialogsProps {
  closeBoardDialogOpen: boolean
  closeDialogError: string | null
  setCloseDialogError: Dispatch<SetStateAction<string | null>>
  cancelBoardClose: () => void
  dialogBusyAction: 'close-discard' | 'close-save' | 'gate-save' | null
  setDialogBusyAction: Dispatch<SetStateAction<'close-discard' | 'close-save' | 'gate-save' | null>>
  nodes: ChecklistItem[]
  persistBoardSnapshot: (nodes: ChecklistItem[]) => Promise<boolean>
  setCloseBoardDialogOpen: Dispatch<SetStateAction<boolean>>
  completeBoardClose: () => void
  restoreBoard: () => void
  openGatesDialog: { intent: 'close' | 'save' } | null
  setOpenGatesDialog: Dispatch<SetStateAction<{ intent: 'close' | 'save' } | null>>
  gateDialogError: string | null
  setGateDialogError: Dispatch<SetStateAction<string | null>>
  buildOpenGateCleanupSnapshot: () => { cleaned: ChecklistItem[]; persistedDeletionIds: string[] }
  pendingDeletionsRef: MutableRefObject<Set<string>>
}

export function BoardDialogs({
  closeBoardDialogOpen,
  closeDialogError,
  setCloseDialogError,
  cancelBoardClose,
  dialogBusyAction,
  setDialogBusyAction,
  nodes,
  persistBoardSnapshot,
  setCloseBoardDialogOpen,
  completeBoardClose,
  restoreBoard,
  openGatesDialog,
  setOpenGatesDialog,
  gateDialogError,
  setGateDialogError,
  buildOpenGateCleanupSnapshot,
  pendingDeletionsRef,
}: BoardDialogsProps) {
  return (
    <>
      <AppDialog
        open={closeBoardDialogOpen}
        title="Unsaved board changes"
        message={closeDialogError ?? 'You have unsaved position/connection changes on this canvas. Save them before closing?'}
        onDismiss={cancelBoardClose}
        actions={[
          {
            label: dialogBusyAction === 'close-save' ? 'Saving...' : 'Save & close',
            variant: 'primary',
            disabled: dialogBusyAction !== null,
            loading: dialogBusyAction === 'close-save',
            onClick: () => {
              if (dialogBusyAction) return
              setCloseDialogError(null)
              setDialogBusyAction('close-save')
              void persistBoardSnapshot(nodes).then((ok) => {
                if (!ok) {
                  setCloseDialogError('Save failed. Your canvas is still open. Try again, or discard these local changes.')
                  return
                }
                setCloseBoardDialogOpen(false)
                completeBoardClose()
              }).finally(() => {
                setDialogBusyAction((current) => current === 'close-save' ? null : current)
              })
            },
          },
          {
            label: dialogBusyAction === 'close-discard' ? 'Discarding...' : 'Discard & close',
            variant: 'danger',
            disabled: dialogBusyAction !== null,
            loading: dialogBusyAction === 'close-discard',
            onClick: () => {
              if (dialogBusyAction) return
              setDialogBusyAction('close-discard')
              setCloseBoardDialogOpen(false)
              restoreBoard()
              completeBoardClose()
            },
          },
          { label: 'Cancel', variant: 'ghost', disabled: dialogBusyAction !== null, onClick: cancelBoardClose },
        ]}
      />
      <AppDialog
        open={openGatesDialog !== null}
        title="You cannot leave a Gate open"
        message={gateDialogError ?? "One or more gates aren't fully wired — they need ≥2 inputs and an outgoing connection. Tidy them up first, or remove them and proceed."}
        onDismiss={cancelBoardClose}
        actions={[
          {
            label: dialogBusyAction === 'gate-save' ? 'Saving...' : 'Remove all open gates and Save anyways',
            variant: 'primary',
            disabled: dialogBusyAction !== null,
            loading: dialogBusyAction === 'gate-save',
            onClick: () => {
              if (dialogBusyAction) return
              const intent = openGatesDialog?.intent
              const { cleaned, persistedDeletionIds } = buildOpenGateCleanupSnapshot()
              persistedDeletionIds.forEach((id) => pendingDeletionsRef.current.add(id))
              setGateDialogError(null)
              setDialogBusyAction('gate-save')
              void persistBoardSnapshot(cleaned).then((ok) => {
                if (!ok) {
                  persistedDeletionIds.forEach((id) => pendingDeletionsRef.current.delete(id))
                  setGateDialogError('Save failed. The open gates were not removed. Try again, or go back and wire/remove the gates manually.')
                  return
                }
                setOpenGatesDialog(null)
                if (intent === 'close') completeBoardClose()
              }).finally(() => {
                setDialogBusyAction((current) => current === 'gate-save' ? null : current)
              })
            },
          },
          { label: 'Go back', variant: 'ghost', disabled: dialogBusyAction !== null, onClick: cancelBoardClose },
        ]}
      />
    </>
  )
}
